import { useEffect, useRef, useState } from "react"
import { useParams } from "react-router-dom"
import { ArrowLeft, Check, ChevronRight, Loader2, Paperclip, Plus, RotateCcw, X } from "lucide-react"
import {
  ACTION_KIND_LABELS,
  CHANNEL_LABELS,
  IDENTITY_LABELS,
  NOTE_LABELS,
  STAGES,
  advanceBlocker,
  enabledStages,
  isOverdue,
  nextStage,
  stageLabel,
  type ActionKind,
  type ComplaintAction,
  type ComplaintDetail,
  type ComplaintLogEntry,
  type NoteField,
  type Stage,
  type StageConfig,
} from "../../shared/domain"
import { Avatar, Empty, ErrorBox, Loading, Panel, SeverityBadge, StatusBadge } from "@/components/common"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { NativeSelect } from "@/components/ui/native-select"
import { Textarea } from "@/components/ui/textarea"
import { apiErrorMessage, type ComplaintEdit } from "@/lib/api"
import { useApp } from "@/lib/app-context"
import { fmtDate, fmtDateTime, fromDateInput, toDateInput, useAsync } from "@/lib/format"
import { cn } from "@/lib/utils"

/** Textos de cada etapa na ficha (o resto da etapa são ações e respostas). */
const STAGE_NOTES: Array<{ field: NoteField; stage: Stage; placeholder: string }> = [
  { field: "analysis", stage: "analise", placeholder: "O que se verificou: factos, lote, encomenda, reclamações semelhantes…" },
  { field: "root_cause", stage: "causa", placeholder: "Causa raiz (5 porquês, Ishikawa…)" },
  { field: "immediate_correction", stage: "acao", placeholder: "Correção imediata ao cliente (substituição, crédito, recolha…)" },
  { field: "response_text", stage: "resposta", placeholder: "Resposta dada ao cliente" },
  { field: "closing_notes", stage: "fecho", placeholder: "Conclusão: procedente / não procedente, custos, notas" },
]

export function ComplaintDetailPage() {
  const { id = "" } = useParams()
  const { api, go } = useApp()
  const { data, error, loading, reload, setData } = useAsync(() => api.complaint(id), [api, id])

  return (
    <div className="space-y-4">
      <button className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground" onClick={() => go("/reclamacoes")}>
        <ArrowLeft className="h-3.5 w-3.5" /> Reclamações
      </button>
      {error ? <ErrorBox message={error} onRetry={reload} /> : null}
      {loading && !data ? <Loading /> : null}
      {data ? <Detail d={data} reload={reload} replace={setData} /> : null}
    </div>
  )
}

function Detail({ d, reload, replace }: { d: ComplaintDetail; reload(): void; replace(d: ComplaintDetail): void }) {
  const { api, toast, settings, users, userName, severityLabel } = useApp()
  const c = d.complaint
  const [busy, setBusy] = useState(false)
  const [cancelOpen, setCancelOpen] = useState(false)
  const [closeOpen, setCloseOpen] = useState(false)
  const closed = c.status !== "open"
  const stages = settings.stages
  const stageIdx = STAGES.indexOf(c.stage)
  const next = nextStage(stages, c.stage)
  // A mesma regra que o servidor aplica — o botão diz porque não avança.
  const blocker = next ? advanceBlocker(c, d.actions.length, stages) : null
  // Reabrir volta à última etapa ativa antes do Fecho.
  const reopenTo = [...enabledStages(stages)].reverse().find((s) => STAGES.indexOf(s) < STAGES.indexOf("fecho")) ?? "registo"
  const label = (s: Stage) => stageLabel(stages, s)

  const run = async (fn: () => Promise<unknown>, ok?: string) => {
    setBusy(true)
    try {
      await fn()
      if (ok) toast("success", ok)
      reload()
    } catch (e) {
      toast("error", "Não foi possível guardar", apiErrorMessage(e))
    } finally {
      setBusy(false)
    }
  }

  const edit = (patch: ComplaintEdit) =>
    run(async () => {
      const updated = await api.updateComplaint(c.id, patch)
      replace({ ...d, complaint: updated })
    })

  return (
    <>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-lg font-semibold">{c.ref}</h2>
            <StatusBadge c={c} />
            <Badge variant={c.identity_status === "verified" ? "success" : c.identity_status === "unverified" ? "warning" : "neutral"}>
              {IDENTITY_LABELS[c.identity_status]}
            </Badge>
          </div>
          <p className="truncate text-sm text-muted-foreground">
            {c.submitted_name ?? "Sem cliente"}
            {c.category ? ` · ${c.category}` : ""}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {c.status === "open" ? (
            <Button variant="outline" size="sm" disabled={busy} onClick={() => setCancelOpen(true)}>
              <X className="h-4 w-4" /> Anular
            </Button>
          ) : null}
          {c.status === "closed" && c.stage !== "eficacia" ? (
            <Button variant="outline" size="sm" disabled={busy} onClick={() => run(() => api.changeStage(c.id, reopenTo, "Reaberta."), "Reclamação reaberta")}>
              <RotateCcw className="h-4 w-4" /> Reabrir
            </Button>
          ) : null}
          {next && c.status !== "cancelled" ? (
            <Button
              size="sm"
              disabled={busy || !!blocker}
              title={blocker ?? undefined}
              onClick={() => (next === "fecho" ? setCloseOpen(true) : run(() => api.changeStage(c.id, next), `Etapa: ${label(next)}`))}
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ChevronRight className="h-4 w-4" />}
              {next === "fecho" ? "Encerrar" : next === "eficacia" ? "Verificar eficácia" : `Avançar para ${label(next)}`}
            </Button>
          ) : null}
        </div>
      </div>
      {blocker && c.status === "open" ? <p className="-mt-1 text-right text-xs text-amber-700">{blocker}</p> : null}

      <Stepper current={c.stage} cancelled={c.status === "cancelled"} stages={stages} />

      {c.status === "closed" && (c.resolution_type || c.resolution_text) ? (
        <section className="rounded-xl border border-green-200 bg-green-50 p-4 text-sm text-green-950">
          <p className="font-medium">
            Resolução{c.resolution_type ? `: ${c.resolution_type}` : ""}
            {c.expectation ? (
              <span className={cn("ml-2 text-xs font-normal", c.expectation === c.resolution_type ? "text-green-700" : "text-amber-700")}>
                {c.expectation === c.resolution_type ? "· igual ao pedido" : `· pedido: ${c.expectation}`}
              </span>
            ) : null}
          </p>
          {c.resolution_text ? <p className="mt-1 whitespace-pre-wrap">{c.resolution_text}</p> : null}
          <p className="mt-1 text-xs text-green-800">Visível ao cliente no acompanhamento.</p>
        </section>
      ) : null}

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="space-y-4 lg:col-span-2">
          <Panel title="Descrição do cliente">
            <p className="whitespace-pre-wrap text-sm">“{c.description}”</p>
            {c.expectation ? <p className="mt-2 text-xs text-muted-foreground">Pretende: {c.expectation}</p> : null}
            {d.lines.length ? (
              <ul className="mt-3 space-y-1 text-xs">
                {d.lines.map((l) => (
                  <li key={l.id}>
                    {l.sku ? `${l.sku} · ` : ""}
                    {l.description} — reclamado {l.qty_claimed ?? "?"} de {l.qty_invoiced ?? "?"} {l.unit ?? ""}
                  </li>
                ))}
              </ul>
            ) : null}
          </Panel>

          <Panel title="Tratamento">
            <div className="space-y-4">
              {STAGE_NOTES.filter(
                (n) =>
                  (enabledStages(stages).includes(n.stage) && STAGES.indexOf(n.stage) <= Math.max(stageIdx, 1)) || c.metadata[n.field],
              ).map((n) => (
                <NoteEditor
                  key={n.field}
                  label={NOTE_LABELS[n.field]}
                  value={(c.metadata[n.field] as string | undefined) ?? ""}
                  placeholder={n.placeholder}
                  disabled={c.status === "cancelled"}
                  onSave={(v) => edit({ notes: { [n.field]: v } })}
                />
              ))}
            </div>
          </Panel>

          <ActionsPanel d={d} onChange={reload} />
        </div>

        <div className="space-y-4">
          <Panel title="Dados">
            <dl className="grid grid-cols-[7rem_1fr] gap-x-3 gap-y-2.5 text-sm">
              <dt className="text-muted-foreground">Cliente</dt>
              <dd className="min-w-0 truncate">{c.submitted_name ?? "—"}</dd>
              <dt className="text-muted-foreground">Contacto</dt>
              <dd className="min-w-0 break-words">
                {[c.submitted_email, c.submitted_phone].filter(Boolean).join(" · ") || "—"}
              </dd>
              <dt className="text-muted-foreground">Canal</dt>
              <dd>{CHANNEL_LABELS[c.channel]}</dd>
              <dt className="text-muted-foreground">Recebida</dt>
              <dd>{fmtDate(c.created_at)}</dd>
              {c.invoice_ref ? (
                <>
                  <dt className="text-muted-foreground">Fatura</dt>
                  <dd>{c.invoice_ref}</dd>
                </>
              ) : null}
              <dt className="self-center text-muted-foreground">Motivo</dt>
              <dd>
                <NativeSelect className="h-8" disabled={closed} value={c.category ?? ""} onChange={(e) => edit({ category: e.target.value || null })}>
                  <option value="">—</option>
                  {[...new Set([...settings.categories, ...(c.category ? [c.category] : [])])].map((x) => (
                    <option key={x}>{x}</option>
                  ))}
                </NativeSelect>
              </dd>
              <dt className="self-center text-muted-foreground">Gravidade</dt>
              <dd>
                {closed ? (
                  <SeverityBadge severity={c.severity} label={severityLabel(c.severity) ?? undefined} />
                ) : (
                  <NativeSelect className="h-8" value={c.severity ?? ""} onChange={(e) => edit({ severity: e.target.value || null })}>
                    <option value="">—</option>
                    {settings.severities.map((s) => (
                      <option key={s.key} value={s.key}>
                        {s.label}
                      </option>
                    ))}
                  </NativeSelect>
                )}
              </dd>
              <dt className="self-center text-muted-foreground">Prazo</dt>
              <dd>
                <Input
                  type="date"
                  className={cn("h-8", isOverdue(c) && "border-red-300 text-red-600")}
                  disabled={closed}
                  value={toDateInput(c.due_at)}
                  onChange={(e) => edit({ due_at: fromDateInput(e.target.value) })}
                />
              </dd>
              <dt className="self-center text-muted-foreground">Responsável</dt>
              <dd>
                <NativeSelect className="h-8" disabled={closed} value={c.owner_user_id ?? ""} onChange={(e) => edit({ owner_user_id: e.target.value || null })}>
                  <option value="">—</option>
                  {users.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.name}
                    </option>
                  ))}
                </NativeSelect>
              </dd>
              {c.closed_at ? (
                <>
                  <dt className="text-muted-foreground">Encerrada</dt>
                  <dd>{fmtDate(c.closed_at)}</dd>
                </>
              ) : null}
            </dl>
            {c.owner_user_id ? (
              <p className="mt-3 flex items-center gap-2 text-xs text-muted-foreground">
                <Avatar name={userName(c.owner_user_id)} /> {userName(c.owner_user_id) ?? "Responsável"}
              </p>
            ) : null}
          </Panel>

          <FilesPanel d={d} onChange={reload} />
          <LogPanel log={d.log} complaintId={c.id} onChange={reload} disabled={c.status === "cancelled"} />
        </div>
      </div>

      <CloseDialog
        open={closeOpen}
        onOpenChange={setCloseOpen}
        resolutions={settings.resolutions}
        initialType={c.resolution_type ?? (c.expectation && settings.resolutions.includes(c.expectation) ? c.expectation : "")}
        initialText={c.resolution_text ?? (c.metadata.response_text as string | undefined) ?? ""}
        expectation={c.expectation}
        onConfirm={(resolution_type, resolution_text) => {
          setCloseOpen(false)
          void run(() => api.changeStage(c.id, "fecho", undefined, { resolution_type, resolution_text }), "Reclamação encerrada")
        }}
      />

      <CancelDialog
        open={cancelOpen}
        onOpenChange={setCancelOpen}
        onConfirm={(reason) => {
          setCancelOpen(false)
          void run(() => api.cancelComplaint(c.id, reason), "Reclamação anulada")
        }}
      />
    </>
  )
}

function Stepper({ current, cancelled, stages }: { current: Stage; cancelled: boolean; stages: Record<Stage, StageConfig> }) {
  const active = enabledStages(stages)
  const idx = active.indexOf(current)
  return (
    <ol className="flex overflow-x-auto rounded-xl border border-border/60 bg-card p-1 shadow-sm">
      {active.map((s, i) => (
        <li
          key={s}
          className={cn(
            "flex min-w-[6.5rem] flex-1 items-center justify-center gap-1.5 rounded-lg px-2 py-2 text-xs font-medium",
            i < idx && "text-accent",
            i === idx && !cancelled && "bg-accent text-accent-foreground shadow-sm",
            i === idx && cancelled && "bg-secondary text-muted-foreground line-through",
            i > idx && "text-muted-foreground",
          )}
        >
          {i < idx ? <Check className="h-3.5 w-3.5" /> : <span className="tabular-nums">{i + 1}</span>}
          {stageLabel(stages, s)}
        </li>
      ))}
    </ol>
  )
}

function NoteEditor({
  label,
  value,
  placeholder,
  disabled,
  onSave,
}: {
  label: string
  value: string
  placeholder: string
  disabled?: boolean
  onSave(v: string): void
}) {
  const [draft, setDraft] = useState(value)
  useEffect(() => setDraft(value), [value])
  const dirty = draft.trim() !== value.trim()
  return (
    <div className="grid gap-1.5">
      <Label>{label}</Label>
      <Textarea rows={3} value={draft} placeholder={placeholder} disabled={disabled} onChange={(e) => setDraft(e.target.value)} />
      {dirty ? (
        <div className="flex justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={() => setDraft(value)}>
            Descartar
          </Button>
          <Button size="sm" onClick={() => onSave(draft)}>
            Guardar
          </Button>
        </div>
      ) : null}
    </div>
  )
}

function ActionsPanel({ d, onChange }: { d: ComplaintDetail; onChange(): void }) {
  const { api, toast, users, userName } = useApp()
  const [adding, setAdding] = useState(false)
  const [kind, setKind] = useState<ActionKind>("corrective")
  const [description, setDescription] = useState("")
  const [owner, setOwner] = useState("")
  const [due, setDue] = useState("")
  const c = d.complaint
  const locked = c.status === "cancelled"

  const save = async (fn: () => Promise<unknown>) => {
    try {
      await fn()
      onChange()
    } catch (e) {
      toast("error", "Não foi possível guardar", apiErrorMessage(e))
    }
  }

  return (
    <Panel
      title="Ações"
      action={
        !locked ? (
          <Button variant="outline" size="sm" onClick={() => setAdding((v) => !v)}>
            <Plus className="h-4 w-4" /> Nova ação
          </Button>
        ) : null
      }
    >
      {adding ? (
        <div className="mb-4 grid gap-2 rounded-lg border border-border/60 bg-secondary/40 p-3">
          <div className="grid gap-2 sm:grid-cols-3">
            <NativeSelect value={kind} onChange={(e) => setKind(e.target.value as ActionKind)}>
              {Object.entries(ACTION_KIND_LABELS).map(([k, l]) => (
                <option key={k} value={k}>
                  {l}
                </option>
              ))}
            </NativeSelect>
            <NativeSelect value={owner} onChange={(e) => setOwner(e.target.value)}>
              <option value="">Responsável</option>
              {users.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name}
                </option>
              ))}
            </NativeSelect>
            <Input type="date" value={due} onChange={(e) => setDue(e.target.value)} />
          </div>
          <Textarea rows={2} placeholder="O que vai ser feito" value={description} onChange={(e) => setDescription(e.target.value)} />
          <div className="flex justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={() => setAdding(false)}>
              Cancelar
            </Button>
            <Button
              size="sm"
              disabled={!description.trim()}
              onClick={() =>
                save(async () => {
                  await api.addAction(c.id, { kind, description, owner_user_id: owner || null, due_at: fromDateInput(due) })
                  setDescription("")
                  setDue("")
                  setAdding(false)
                })
              }
            >
              Adicionar
            </Button>
          </div>
        </div>
      ) : null}

      {d.actions.length === 0 ? (
        <Empty>Sem ações registadas.</Empty>
      ) : (
        <ul className="space-y-3">
          {d.actions.map((a) => (
            <ActionItem key={a.id} a={a} locked={locked} ownerName={userName(a.owner_user_id)} onSave={(patch) => save(() => api.updateAction(c.id, a.id, patch))} />
          ))}
        </ul>
      )}
    </Panel>
  )
}

function ActionItem({
  a,
  locked,
  ownerName,
  onSave,
}: {
  a: ComplaintAction
  locked: boolean
  ownerName: string | null
  onSave(patch: Partial<ComplaintAction>): void
}) {
  const [notes, setNotes] = useState(a.effectiveness_notes ?? "")
  const overdue = a.status === "open" && a.due_at && new Date(a.due_at).getTime() < Date.now()
  return (
    <li className="rounded-lg border border-border/60 p-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-xs font-medium text-muted-foreground">{ACTION_KIND_LABELS[a.kind]}</p>
          <p className={cn("text-sm", a.status === "cancelled" && "line-through text-muted-foreground")}>{a.description}</p>
          <p className="mt-1 text-xs text-muted-foreground">
            {ownerName ?? "Sem responsável"} · prazo <span className={cn(overdue && "font-medium text-red-600")}>{fmtDate(a.due_at)}</span>
            {a.done_at ? ` · concluída ${fmtDate(a.done_at)}` : ""}
          </p>
        </div>
        {!locked ? (
          <NativeSelect className="h-8 w-36" value={a.status} onChange={(e) => onSave({ status: e.target.value as ComplaintAction["status"] })}>
            <option value="open">Em curso</option>
            <option value="done">Concluída</option>
            <option value="cancelled">Cancelada</option>
          </NativeSelect>
        ) : null}
      </div>
      {a.kind !== "immediate" && a.status === "done" ? (
        <div className="mt-3 grid gap-2 border-t border-border/60 pt-3">
          <p className="text-xs font-medium">Verificação de eficácia</p>
          <div className="flex flex-wrap items-center gap-2">
            <NativeSelect
              className="h-8 w-44"
              disabled={locked}
              value={a.effectiveness_result ?? ""}
              onChange={(e) =>
                onSave({ effectiveness_result: (e.target.value || null) as ComplaintAction["effectiveness_result"], effectiveness_notes: notes })
              }
            >
              <option value="">Por verificar</option>
              <option value="effective">Eficaz</option>
              <option value="not_effective">Não eficaz</option>
            </NativeSelect>
            {a.effectiveness_checked_at ? <span className="text-xs text-muted-foreground">verificada {fmtDate(a.effectiveness_checked_at)}</span> : null}
          </div>
          <div className="flex gap-2">
            <Input className="h-8" placeholder="Evidência (ex.: zero reclamações em 3 meses)" value={notes} disabled={locked} onChange={(e) => setNotes(e.target.value)} />
            {notes !== (a.effectiveness_notes ?? "") ? (
              <Button size="sm" onClick={() => onSave({ effectiveness_notes: notes })}>
                Guardar
              </Button>
            ) : null}
          </div>
        </div>
      ) : null}
    </li>
  )
}

function FilesPanel({ d, onChange }: { d: ComplaintDetail; onChange(): void }) {
  const { api, toast } = useApp()
  const input = useRef<HTMLInputElement>(null)
  const [uploading, setUploading] = useState(false)
  const c = d.complaint

  const upload = async (files: FileList | null) => {
    if (!files?.length) return
    setUploading(true)
    try {
      for (const f of Array.from(files)) await api.attachFile(c.id, f)
      onChange()
    } catch (e) {
      toast("error", "Falha no anexo", apiErrorMessage(e))
    } finally {
      setUploading(false)
      if (input.current) input.current.value = ""
    }
  }

  const open = async (fileId: string) => {
    try {
      const { url } = await api.fileUrl(c.id, fileId)
      window.open(url, "_blank", "noopener")
    } catch (e) {
      toast("error", "Não foi possível abrir", apiErrorMessage(e))
    }
  }

  return (
    <Panel
      title="Anexos"
      action={
        c.status !== "cancelled" ? (
          <Button variant="outline" size="sm" disabled={uploading} onClick={() => input.current?.click()}>
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Paperclip className="h-4 w-4" />} Anexar
          </Button>
        ) : null
      }
    >
      <input ref={input} type="file" multiple className="hidden" onChange={(e) => upload(e.target.files)} />
      {d.files.length === 0 ? (
        <Empty>Sem anexos.</Empty>
      ) : (
        <ul className="space-y-1.5 text-sm">
          {d.files.map((f) => (
            <li key={f.id} className="flex items-center justify-between gap-2">
              <button className="min-w-0 truncate text-left text-accent hover:underline" onClick={() => open(f.id)}>
                {f.file_name ?? "anexo"}
              </button>
              <span className="shrink-0 text-xs text-muted-foreground">
                {f.uploaded_by_kind === "customer" ? "cliente · " : ""}
                {fmtDate(f.created_at)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  )
}

const LOG_LABELS: Record<string, string> = {
  created: "Registada",
  updated: "Dados alterados",
  stage_changed: "Mudança de etapa",
  cancelled: "Anulada",
  note: "Nota",
  action_added: "Ação criada",
  action_updated: "Ação atualizada",
  effectiveness_checked: "Eficácia verificada",
  file_added: "Anexo",
  reply: "Mensagem do cliente",
}

const FIELD_LABELS: Record<string, string> = {
  category: "motivo",
  severity: "gravidade",
  owner_user_id: "responsável",
  due_at: "prazo",
  expectation: "pretensão",
  stage: "etapa",
  status: "estado",
  ...NOTE_LABELS,
}

function LogPanel({ log, complaintId, onChange, disabled }: { log: ComplaintLogEntry[]; complaintId: string; onChange(): void; disabled: boolean }) {
  const { api, toast } = useApp()
  const [note, setNote] = useState("")
  const [isPublic, setIsPublic] = useState(false)
  const [saving, setSaving] = useState(false)

  const add = async () => {
    setSaving(true)
    try {
      await api.addNote(complaintId, note, isPublic ? "public" : "internal")
      setNote("")
      onChange()
    } catch (e) {
      toast("error", "Não foi possível guardar a nota", apiErrorMessage(e))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Panel title="Histórico">
      {!disabled ? (
        <div className="mb-4 grid gap-2">
          <Textarea rows={2} placeholder="Nota (contacto com o cliente, decisão…)" value={note} onChange={(e) => setNote(e.target.value)} />
          <div className="flex items-center justify-between gap-2">
            <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <input type="checkbox" checked={isPublic} onChange={(e) => setIsPublic(e.target.checked)} /> Visível ao cliente
            </label>
            <Button size="sm" disabled={!note.trim() || saving} onClick={add}>
              Adicionar nota
            </Button>
          </div>
        </div>
      ) : null}
      <ol className="space-y-3 border-l border-border pl-3">
        {log.map((e) => (
          <li key={e.id} className="relative text-sm">
            <span className="absolute -left-[17px] top-1.5 h-2 w-2 rounded-full bg-accent" />
            <p className="text-xs text-muted-foreground">
              {fmtDateTime(e.at)} · {e.actor_kind === "customer" ? "Cliente" : e.actor_kind === "system" ? "Sistema" : (e.actor_label ?? "Utilizador")}
              {e.visibility === "public" ? " · visível ao cliente" : ""}
            </p>
            <p className="font-medium">{LOG_LABELS[e.action] ?? e.action}</p>
            {e.message ? <p className="whitespace-pre-wrap text-muted-foreground">{e.message}</p> : null}
            {e.changes && e.action === "updated" ? (
              <p className="text-xs text-muted-foreground">Alterado: {Object.keys(e.changes).map((k) => FIELD_LABELS[k] ?? k).join(", ")}</p>
            ) : null}
          </li>
        ))}
      </ol>
    </Panel>
  )
}

/** Encerrar: tipo de resolução (a lista do portal) + texto que o cliente lê no acompanhamento. */
function CloseDialog({
  open,
  onOpenChange,
  resolutions,
  initialType,
  initialText,
  expectation,
  onConfirm,
}: {
  open: boolean
  onOpenChange(v: boolean): void
  resolutions: string[]
  initialType: string
  initialText: string
  expectation: string | null
  onConfirm(type: string, text: string): void
}) {
  const [type, setType] = useState(initialType)
  const [text, setText] = useState(initialText)
  useEffect(() => {
    if (open) {
      setType(initialType)
      setText(initialText)
    }
  }, [open, initialType, initialText])
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Encerrar reclamação</DialogTitle>
        </DialogHeader>
        <div className="grid gap-3">
          <div className="grid gap-1.5">
            <Label>Tipo de resolução</Label>
            <NativeSelect value={type} onChange={(e) => setType(e.target.value)}>
              <option value="">—</option>
              {resolutions.map((r) => (
                <option key={r}>{r}</option>
              ))}
            </NativeSelect>
            {expectation ? <p className="text-xs text-muted-foreground">O cliente pediu: {expectation}</p> : null}
          </div>
          <div className="grid gap-1.5">
            <Label>Resolução (o cliente vê este texto)</Label>
            <Textarea rows={5} value={text} onChange={(e) => setText(e.target.value)} placeholder="O que foi decidido e feito para o cliente." />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Voltar
          </Button>
          <Button disabled={!type || !text.trim()} onClick={() => onConfirm(type, text.trim())}>
            Encerrar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function CancelDialog({ open, onOpenChange, onConfirm }: { open: boolean; onOpenChange(v: boolean): void; onConfirm(reason: string): void }) {
  const [reason, setReason] = useState("")
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Anular reclamação</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground">Fica no histórico com o motivo; não é apagada.</p>
        <Textarea rows={3} placeholder="Motivo (duplicada, registo indevido…)" value={reason} onChange={(e) => setReason(e.target.value)} />
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Voltar
          </Button>
          <Button variant="destructive" disabled={!reason.trim()} onClick={() => onConfirm(reason)}>
            Anular
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
